/**
 * Urdu numbers charts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_indic_charts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const urduGintiChartsPack: ToilePack = {
    slug: 'urdu-ginti-charts',
    category: 'classroom',
    name: 'Urdu numbers charts',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'hundred', label: '1 to 100', subject: 'a chart of Urdu numerals 1 to 100 in a grid'},
        {key: 'names', label: 'Number names', subject: 'a chart of 1 to 20 with Urdu numerals and number names'},
        {key: 'pictures', label: 'With pictures', subject: 'a chart of 1 to 10 in Urdu with groups of objects'},
    ],
    styles: [
        {key: 'classic', label: 'Classic ruled', style: 'a clean school number chart with thin ruled black lines and bold black numerals', palette: 'black ink on white paper with one accent colour for the heading', framing: 'the chart filling the page inside an even margin'},
        {key: 'rainbow', label: 'Rainbow classroom', style: 'a cheerful classroom wall chart with each row on a different bright colour band and rounded friendly numerals', palette: 'saturated red, orange, yellow, green, blue and purple bands', framing: 'the chart filling the page inside a bold coloured border and a margin'},
        {key: 'pastel', label: 'Soft pastel', style: 'a gentle nursery chart of soft rounded tiles with large numerals and a small doodle border', palette: 'soft pastel pink, mint, butter yellow and sky blue on cream', framing: 'centered on the page with a wide margin'},
    ],
    directions: ['large print', 'black and white', 'with border'],
    directionHint: 'try: large print',
    seo: {
        title: 'Urdu Numbers Charts: 1 to 100, Number Names, Pictures',
        metaDescription: 'Nine printable Urdu number pages: 1 to 100 in a grid, number names from 1 to 20 and a picture chart, in ruled, rainbow and pastel styles.',
        h1: 'Urdu numbers charts: 1 to 100, number names, counting with pictures',
        tagline: 'Nine pages in three styles, each style with a 1 to 100 grid, a number-names chart and a picture chart.',
        intro: 'Parents and teachers print Urdu numbers to teach children to count and read numerals. The nine pages show Urdu numerals from 1 to 100 in a grid, 1 to 20 with the numerals and their number names, and 1 to 10 with groups of objects to count. Each comes as a ruled classroom sheet, a rainbow chart or a soft pastel nursery chart. Type a twist, such as large print or black and white, and the same layouts carry it.',
        useCases: [
            'a classroom number wall',
            'a counting sheet at home',
            'a nursery wall',
            'a daily practice sheet',
        ],
        faq: [
            {q: 'Does the chart go up to 100?', a: 'Yes. One page shows Urdu numerals from 1 to 100 in a grid.'},
            {q: 'Are there pictures to count?', a: 'Yes. One page shows 1 to 10 in Urdu with groups of objects.'},
            {q: 'What is the difference between the three styles?', a: 'Classic ruled uses thin black lines and bold numerals. Rainbow classroom gives each row its own bright band inside a bold border. Soft pastel uses soft rounded tiles on cream with a small doodle border.'},
            {q: 'Can I change the chart or ask for a twist?', a: 'Yes. Type the change, such as large print, black and white or a border. Your request shapes the result; it is not a promise of what these nine pages show.'},
        ],
    },
}
